import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import * as httpPaths from '../../../tools/constants/http-paths';
import * as errors from '../errors';
import { removeIndexHtmlExtension } from '../utils';

import { RouteContext, initialClientRouteContextValue } from './RouteContext';

import type { DataManifest } from '../../../tools/routes';
import type { ClientRouteContextValue } from '../types';
import type { ReactNode } from 'react';
import type { BloodhoundDocument } from 'src/content-types';

export interface ClientRouteProviderProps {
  children: ReactNode;
}

declare global {
  interface Window {
    __ROUTES_STATE__: ClientRouteContextValue;
  }
}

export function ClientRouteProvider({ children }: ClientRouteProviderProps) {
  const location = useLocation();
  const [routeState, setRouteState] = useState(
    window.__ROUTES_STATE__ ?? initialClientRouteContextValue
  );

  const pathname = removeIndexHtmlExtension(location.pathname);
  const currentData = routeState[pathname];

  const currentDocument = currentData?.document;
  const currentLoading = currentData?.loading;
  const currentError = currentData?.error;
  const currentExists = currentData?.exists;

  useEffect(() => {
    if (
      currentDocument ||
      currentLoading ||
      currentError ||
      currentExists === false
    ) {
      return;
    }

    const get = async () => {
      setRouteState(prevState => {
        return {
          ...prevState,
          [pathname]: {
            ...prevState[pathname],
            loading: true,
          },
        };
      });

      try {
        const manifestResponse = await fetch(httpPaths.DATA_MANIFEST_URL, {
          cache: 'no-cache',
        });

        if (manifestResponse.status < 200 || manifestResponse.status >= 300) {
          setRouteState(prevState => {
            return {
              ...prevState,
              [pathname]: {
                ...prevState[pathname],
                loading: false,
                error: new Error(errors.NO_MANIFEST_ERROR),
              },
            };
          });

          return;
        }

        const manifest = (await manifestResponse.json()) as DataManifest;
        const dataUrl = manifest[pathname];

        if (!dataUrl) {
          setRouteState(prevState => {
            return {
              ...prevState,
              [pathname]: {
                ...prevState[pathname],
                loading: false,
                exists: false,
                error: new Error(errors.NO_PAGE_ERROR),
              },
            };
          });

          return;
        }

        const dataResponse = await fetch(dataUrl);

        if (dataResponse.status < 200 || dataResponse.status >= 300) {
          setRouteState(prevState => {
            return {
              ...prevState,
              [pathname]: {
                ...prevState[pathname],
                loading: false,
                error: new Error(errors.NO_DATA_ERROR),
              },
            };
          });

          return;
        }

        const document = (await dataResponse.json()) as BloodhoundDocument;

        setRouteState(prevState => {
          return {
            ...prevState,
            [pathname]: {
              ...prevState[pathname],
              loading: false,
              document,
            },
          };
        });
      } catch (caughtError: unknown) {
        const error =
          caughtError instanceof Error
            ? caughtError
            : new Error(errors.UNKNOWN_ERROR);

        setRouteState(prevState => {
          return {
            ...prevState,
            [pathname]: {
              ...prevState[pathname],
              loading: false,
              error,
            },
          };
        });
      }
    };

    void get();
  }, [pathname, currentDocument, currentLoading, currentError, currentExists]);

  return (
    <RouteContext.Provider value={routeState}>{children}</RouteContext.Provider>
  );
}
